/**
 * Sprint 946G — Cross-origin fetch rewrite (production safety net).
 *
 * Why: Emergent's deploy bakes REACT_APP_BACKEND_URL = "https://seller-submit-hub.emergent.host"
 * into the production bundle, but the frontend is served from the user's
 * branded apex (e.g. studioplaz.com). 123 components call
 * `fetch(\`${process.env.REACT_APP_BACKEND_URL}/api/…\`, { credentials: "include" })`
 * — those become cross-origin under withCredentials + backend
 * `allow_origins=["*"]`, which the browser rejects per CORS spec.
 * Result: every API call surfaces as a generic "Network Error".
 *
 * Emergent's ingress also routes `/api/*` from the apex domain directly
 * to the backend, so falling back to relative paths is always safe.
 *
 * What this does: detects the mismatch at boot, then transparently
 * strips the env-baked host prefix from any fetch URL so the request
 * becomes same-origin (and `withCredentials` works).  Loaded as the
 * very first import in index.js so it patches before any React module
 * issues a network call.
 *
 * Idempotent: re-running this module is a no-op (uses a marker flag).
 * Off in dev/preview when env-host == page-host (same-origin already).
 */
const MARKER = "__splaz_fetch_rewrite_installed__";

(function installFetchRewrite() {
  if (typeof window === "undefined") return;
  if (window[MARKER]) return;
  const envUrl = (process.env.REACT_APP_BACKEND_URL || "").trim();
  if (!envUrl) return;

  let envHost = "";
  try {
    envHost = new URL(envUrl).host.toLowerCase();
  } catch {
    return;
  }
  const pageHost = (window.location.host || "").toLowerCase();
  if (!envHost || !pageHost || envHost === pageHost) return;

  const prefix = envUrl.replace(/\/+$/, "");

  // 1. Patch window.fetch
  if (typeof window.fetch === "function") {
    const _origFetch = window.fetch.bind(window);
    window.fetch = function patchedFetch(input, init) {
      try {
        if (typeof input === "string" && input.startsWith(prefix)) {
          const rewritten = input.slice(prefix.length) || "/";
          return _origFetch(rewritten, init);
        }
        if (input && typeof Request !== "undefined" && input instanceof Request && input.url.startsWith(prefix)) {
          const rewritten = input.url.slice(prefix.length) || "/";
          return _origFetch(new Request(rewritten, input), init);
        }
      } catch { /* fall through to original fetch */ }
      return _origFetch(input, init);
    };
  }

  // 2. Patch XMLHttpRequest.open (covers any non-axios XHR usage).
  //    axios is already handled by lib/api.js's _resolveBackendUrl(),
  //    but defense in depth is cheap.
  if (typeof window.XMLHttpRequest === "function") {
    const proto = window.XMLHttpRequest.prototype;
    const _origOpen = proto.open;
    proto.open = function patchedOpen(method, url, ...rest) {
      try {
        if (typeof url === "string" && url.startsWith(prefix)) {
          url = url.slice(prefix.length) || "/";
        }
      } catch { /* noop */ }
      return _origOpen.call(this, method, url, ...rest);
    };
  }

  window[MARKER] = { envHost, pageHost, prefix, installedAt: new Date().toISOString() };
  // eslint-disable-next-line no-console
  console.info(`[splaz] cross-origin fetch rewrite active: ${envHost} → relative (page=${pageHost})`);
})();

export const FETCH_REWRITE_STATUS = (typeof window !== "undefined" && window[MARKER]) || null;
